import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      if (!authService.isLoggedIn()) {
        if (!cancelled) setAuthReady(true);
        return;
      }

      try {
        const user = await authService.validateSession();
        if (!cancelled) setCurrentUser(user);
      } catch (error) {
        console.warn('Session expired or invalid; clearing login.', error);
        authService.logout();
        if (!cancelled) setCurrentUser(null);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithPassword = useCallback(async (username, password) => {
    const user = await authService.login({ username, password });
    setCurrentUser(user);
    return user;
  }, []);

  const loginWithApiKey = useCallback(async (apiKey) => {
    const user = await authService.loginWithApiKey({ apiKey });
    setCurrentUser(user);
    return user;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setCurrentUser(null);
  }, []);

  const isLoggedIn = Boolean(currentUser);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoggedIn,
        authReady,
        loginWithPassword,
        loginWithApiKey,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
